import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, Truck, ShieldCheck, RotateCcw, Instagram, Facebook, Twitter } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Magnetic, Logo } from '@/components/motion/Primitives';

const LINKS = {
    Shop: [
        { label: 'All Products', to: '/shop' },
        { label: 'Laundry Care', to: '/shop?category=Laundry%20Care' },
        { label: 'Dishwashing', to: '/shop?category=Dishwashing' },
        { label: 'Surface & Glass', to: '/shop?category=Surface%20%26%20Glass' },
        { label: 'Bathroom & Utility', to: '/shop?category=Bathroom%20%26%20Utility' },
        { label: 'Hand Wash', to: '/shop?category=Hand%20Wash' },
    ],
    Company: [
        { label: 'Our Story', to: '/#why' },
        { label: 'Why XTRA', to: '/#why' },
        { label: 'Reviews', to: '/#reviews' },
        { label: 'Admin', to: '/admin' },
    ],
};

export default function Footer() {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);

    const subscribe = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            const { data } = await api.post('/newsletter', { email });
            toast.success(data.message || 'Subscribed!');
            setEmail('');
        } catch {
            toast.error('Please enter a valid email.');
        } finally { setLoading(false); }
    };

    return (
        <footer className="relative overflow-hidden bg-[#0A0173] text-white">
            <div className="noise-overlay" />
            {/* Guarantee strip */}
            <div className="relative border-b border-white/10">
                <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 py-7 grid grid-cols-1 sm:grid-cols-3 gap-6">
                    {[{ icon: Truck, t: 'Free shipping', s: 'On orders over \u20b9499' }, { icon: ShieldCheck, t: 'Safe & tested', s: 'ISO-certified quality' }, { icon: RotateCcw, t: 'Easy 7-day returns', s: 'On unopened products' }].map((f, i) => (
                        <div key={i} className="flex items-center gap-3.5">
                            <span className="h-11 w-11 rounded-full bg-[var(--xtra-yellow)] grid place-items-center shrink-0"><f.icon className="h-5 w-5 text-black" /></span>
                            <span><span className="block font-display font-bold">{f.t}</span><span className="block text-sm text-white/60">{f.s}</span></span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="relative max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 py-14 lg:py-20">
                {/* Big brand statement + newsletter */}
                <div className="grid lg:grid-cols-[1.3fr_1fr] gap-10 lg:gap-16 pb-14 border-b border-white/10">
                    <div>
                        <Logo variant="dark" className="h-8" />
                        <h2 className="mt-6 font-display font-extrabold tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">
                            Har Din,<br /><span className="text-[var(--xtra-yellow)]">Thoda Xtra.</span>
                        </h2>
                        <p className="mt-5 text-white/70 max-w-md leading-relaxed">Home care that gives back a little Xtra in every wash — Xtra clean, Xtra value, Xtra care. Trusted by Indian families since 1987.</p>
                    </div>
                    <div className="lg:pt-4">
                        <h3 className="font-display font-bold text-xl">Join the xtra family</h3>
                        <p className="mt-2 text-white/70 text-sm">Cleaning tips & exclusive offers. No spam, ever.</p>
                        <form onSubmit={subscribe} className="mt-5 flex gap-2">
                            <input
                                data-testid="newsletter-email-input"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@email.com"
                                className="flex-1 h-12 rounded-full px-5 bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-[var(--xtra-yellow)]"
                            />
                            <Magnetic strength={0.2}>
                                <button data-testid="newsletter-submit-button" disabled={loading} className="btn-accent h-12 px-6 disabled:opacity-70">{loading ? '\u2026' : (<>Join <ArrowRight className="h-4 w-4" /></>)}</button>
                            </Magnetic>
                        </form>
                        <div className="mt-6 flex items-center gap-3">
                            {[Instagram, Facebook, Twitter].map((Icon, i) => (
                                <span key={i} className="h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 transition-colors grid place-items-center cursor-pointer"><Icon className="h-5 w-5" /></span>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Link columns */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 pt-12">
                    {Object.entries(LINKS).map(([title, items]) => (
                        <div key={title}>
                            <h4 className="font-display font-bold text-[var(--xtra-yellow)]">{title}</h4>
                            <ul className="mt-4 space-y-2.5">
                                {items.map((l) => (
                                    <li key={l.label}><Link to={l.to} className="text-white/70 hover:text-white link-underline transition-colors text-sm">{l.label}</Link></li>
                                ))}
                            </ul>
                        </div>
                    ))}
                    <div>
                        <h4 className="font-display font-bold text-[var(--xtra-yellow)]">Support</h4>
                        <ul className="mt-4 space-y-2.5 text-sm text-white/70">
                            <li>care@xtra.in</li>
                            <li>1800-123-XTRA</li>
                            <li>Mon–Sat, 9am–6pm</li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-display font-bold text-[var(--xtra-yellow)]">We accept</h4>
                        <div className="mt-4 flex flex-wrap gap-2">
                            {['UPI', 'VISA', 'RuPay', 'Master', 'COD'].map((p) => (
                                <span key={p} className="px-2.5 py-1.5 rounded-lg bg-white/10 text-[11px] font-bold tracking-wide">{p}</span>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-white/55">
                    <p className="inline-flex items-center gap-2"><Sparkles className="h-4 w-4 text-[var(--xtra-yellow)]" /> © {new Date().getFullYear()} XTRA. All rights reserved.</p>
                    <div className="flex items-center gap-5">
                        <span className="hover:text-white cursor-pointer transition-colors">Privacy</span>
                        <span className="hover:text-white cursor-pointer transition-colors">Terms</span>
                        <span className="hover:text-white cursor-pointer transition-colors">Shipping</span>
                    </div>
                </div>
            </div>
        </footer>
    );
}
